import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";

import {
  CreateJobTitleDocument,
  DeleteJobTitleDocument,
  JobTitlesDocument,
  UpdateJobTitleDocument,
} from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, SettingsEvent, trackEvent } from "#app/lib/analytics";

const trackJobTitle = (
  event: (typeof SettingsEvent)[keyof typeof SettingsEvent],
  data?: Record<string, unknown>,
) => trackEvent({ context: AnalyticsContext.Settings, event, label: "job_title", ...(data ? { data } : {}) });

interface WorkspaceJobTitle {
  id: string;
  name: string;
  createdBy: string;
}

/**
 * Workspace job-title vocabulary. Loosely coupled to `user.title` — these are
 * shared, reusable suggestions any member can add and the creator (or an admin)
 * can remove. Deleting one never touches users who already carry that title.
 */
export function useJobTitles() {
  const { data } = useQuery(JobTitlesDocument);
  const titles = useMemo<WorkspaceJobTitle[]>(
    () => data?.jobTitles ?? [],
    [data],
  );

  const [createMutation] = useMutation(CreateJobTitleDocument);
  const [updateMutation] = useMutation(UpdateJobTitleDocument);
  const [deleteMutation] = useMutation(DeleteJobTitleDocument);

  // Adds a title to the shared vocabulary. Idempotent server-side. Refetches so
  // the new suggestion is immediately available to every picker.
  const createTitle = useCallback(
    async (name: string): Promise<boolean> => {
      const trimmed = name.trim();
      if (!trimmed) return false;
      try {
        const { data } = await createMutation({
          variables: { input: { name: trimmed } },
          refetchQueries: [{ query: JobTitlesDocument }],
          awaitRefetchQueries: true,
        });
        const res = data?.createJobTitle;
        if (res && res.__typename === "ValidationError") {
          toastManager.add({
            title: res.message ?? "Failed to add title",
            type: "error",
          });
          return false;
        }
        trackJobTitle(SettingsEvent.Create);
        return true;
      } catch (err) {
        toastMutationError(err, "Failed to add title");
        return false;
      }
    },
    [createMutation],
  );

  // Renames a vocabulary entry. Vocabulary-only — members already carrying
  // the old name keep it (`user.title` is a free string).
  const renameTitle = useCallback(
    async (id: string, name: string): Promise<boolean> => {
      const trimmed = name.trim();
      if (!trimmed) return false;
      try {
        const { data } = await updateMutation({
          variables: { titleId: id, input: { name: trimmed } },
          refetchQueries: [{ query: JobTitlesDocument }],
          awaitRefetchQueries: true,
        });
        const res = data?.updateJobTitle;
        if (
          res &&
          (res.__typename === "NotFoundError" ||
            res.__typename === "PermissionDenied" ||
            res.__typename === "ValidationError")
        ) {
          toastManager.add({
            title: res.message ?? "Failed to rename title",
            type: "error",
          });
          return false;
        }
        trackJobTitle(SettingsEvent.Update);
        return true;
      } catch (err) {
        toastMutationError(err, "Failed to rename title");
        return false;
      }
    },
    [updateMutation],
  );

  const deleteTitle = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const { data } = await deleteMutation({
          variables: { titleId: id },
          refetchQueries: [{ query: JobTitlesDocument }],
          awaitRefetchQueries: true,
        });
        const res = data?.deleteJobTitle;
        if (
          res &&
          (res.__typename === "NotFoundError" ||
            res.__typename === "PermissionDenied")
        ) {
          toastManager.add({
            title: res.message ?? "Failed to remove title",
            type: "error",
          });
          return false;
        }
        trackJobTitle(SettingsEvent.Delete);
        return true;
      } catch (err) {
        toastMutationError(err, "Failed to remove title");
        return false;
      }
    },
    [deleteMutation],
  );

  return { titles, createTitle, renameTitle, deleteTitle };
}
